import {
  Button,
  Checkbox,
  FormControlLabel,
  makeStyles,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  Table,
  Box,
  Typography,
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import clsx from "clsx";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getFileCellValues } from "./helpers";
import safeLogger from "src/services/safeLogger";
import { CustomTextField } from "./sampleFilesTable";
import SaveIcon from "@material-ui/icons/Save";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";

const useStyles = makeStyles((theme) => ({
  hidden: {
    display: "none",
  },
  metaSummary: {
    marginBottom: "20px",
    display: "flex",
    "& p": {
      display: "inline-flex",
    },
  },
  alert: {},
}));

const ACNVSampleFileTableRow = (props) => {
  const {
    headers,
    sequencerType,
    index,
    fileMeta,
    setFileMeta,
    formError,
    setFormError,
  } = props;

  const updateFileMeta = (name, value) => {
    let newFileMeta = fileMeta.slice(0);
    newFileMeta[index][name] = value;
    setFileMeta(newFileMeta);
  };

  return (
    <TableRow>
      {Object.keys(headers).map((header, i) => (
        <TableCell key={i}>
          <CustomTextField
            rowIndex={index}
            inputIndex={i}
            sequencerType={sequencerType}
            label={headers[header]}
            name={header}
            value={fileMeta[index][header] ? fileMeta[index][header] : ""}
            updateFileMeta={updateFileMeta}
            formError={formError}
            setFormError={setFormError}
          />
        </TableCell>
      ))}

      <TableCell>
        {fileMeta[index]["filename"] ? fileMeta[index]["filename"] : ""}
      </TableCell>

      <TableCell>
        <FormControlLabel
          onChange={(event, checked) => {
            updateFileMeta(event.target.name, checked);
          }}
          checked={fileMeta[index].calculate_frequency ?? true}
          control={<Checkbox name="calculate_frequency" />}
        />
      </TableCell>
    </TableRow>
  );
};

export const ACNVModelBamSampleFilesTable = (props) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [formError, setFormError] = useState([]);
  const { appConf, formData, files, fileMeta, setFileMeta, phase, setPhase } =
    props;

  const [headers, setHeaders] = useState(undefined);
  const { t } = useTranslation();

  useEffect(() => {
    if (fileMeta.length && formError.length !== fileMeta.length) {
      safeLogger(fileMeta);
      for (let i = 0; i < fileMeta.length; i++) {
        formError.push({});
      }
      setFormError(formError.slice(0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileMeta]);

  useEffect(() => {
    if (headers) {
      let tmpFileObjects = [];

      for (let i = 0; i < files.length; i++) {
        let fileObj = {};
        const cellValues = getFileCellValues(files[i].name);
        if (cellValues) {
          Object.keys(headers).map(
            (headItem, index) => (fileObj[headItem] = cellValues[index]),
          );
        }
        fileObj["byte_size"] = files[i].size;
        fileObj["filename"] = files[i].name;
        // default values for lane
        fileObj["lane"] = "L01";
        fileObj["calculate_frequency"] = true;

        tmpFileObjects.push(fileObj);
      }

      setFileMeta([...tmpFileObjects]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]);

  useEffect(() => {
    if (phase === "sampleMeta") {
      if (!headers) {
        setHeaders({
          sample_name: "Sample Name",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const isInputFormatValid = () => {
    for (let i = 0; i < formError.length; i++) {
      const formErrorKeys = Object.keys(formError[i]);
      for (let j = 0; j < formErrorKeys.length; j++) {
        if (formError[i][formErrorKeys[j]]) return false;
      }
    }

    return true;
  };

  const isFileConfigUnique = () => {
    const configValues = [];
    for (let i = 0; i < fileMeta.length; i++) {
      const fileConfig = fileMeta[i].sample_name;
      if (!configValues.includes(fileConfig)) {
        configValues.push(fileConfig);
      } else {
        return false;
      }
    }

    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isInputFormatValid()) {
      if (isFileConfigUnique()) {
        setPhase("credit_calculate");
      } else {
        enqueueSnackbar(
          t(
            "form_conf_not_unique_message",
            "Form configuration is not unique. Please check and try again.",
          ),
          {
            variant: "error",
          },
        );
      }
    } else {
      enqueueSnackbar(
        t(
          "form_input_not_valid_message",
          "Form input are not valid. Please check and try again.",
        ),
        {
          variant: "error",
        },
      );
    }
  };

  return (
    <Grid item xs={12} className={clsx({ [classes.hidden]: phase === "init" })}>
      <Box className={classes.metaSummary}>
        <Typography>
          <span style={{ color: "#5e5e5e" }}>{t("Title")}:</span>
          <span style={{ marginLeft: "4px", marginRight: "20px" }}>
            {formData.name}
          </span>
        </Typography>
        <Typography>
          <span style={{ color: "#5e5e5e" }}>{t("Kit")}:</span>
          <span style={{ marginLeft: "4px", marginRight: "20px" }}>
            {appConf.entities.kits[formData.kit]?.label}
          </span>
        </Typography>
        <Typography>
          <span style={{ color: "#5e5e5e" }}>{t("Sequencer")}:</span>
          <span style={{ marginLeft: "4px", marginRight: "20px" }}>
            {appConf.entities.sequencers[formData.sequencer]}
          </span>
        </Typography>
      </Box>
      {fileMeta.length ? (
        <form onSubmit={handleSubmit}>
          <Table>
            <TableHead>
              <TableRow>
                {headers &&
                  Object.values(headers).map((item, index) => (
                    <TableCell key={index}>{item}</TableCell>
                  ))}
                <TableCell>{t("File name")}</TableCell>
                <TableCell>{t("Variant Frequency Calculation")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fileMeta.map((file, index) => {
                return (
                  <ACNVSampleFileTableRow
                    key={index}
                    index={index}
                    headers={headers}
                    sequencerType={formData.sequencer}
                    fileMeta={fileMeta}
                    setFileMeta={setFileMeta}
                    formError={formError}
                    setFormError={setFormError}
                  />
                );
              })}
            </TableBody>
          </Table>
          <Box display="flex" mt={5}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<NavigateBeforeIcon />}
              onClick={() => setPhase("init")}
            >
              {t("Back")}
            </Button>
            <Button
              type="submit"
              style={{ marginLeft: "auto" }}
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
            >
              {t("Submit")}
            </Button>
          </Box>
        </form>
      ) : (
        ""
      )}
    </Grid>
  );
};
